import React, { Suspense, useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { useOrg } from '../data/orgs';
import { useToast } from '../components/Toast';
import { docTypeQueue, docTypeAction, isAiConfigured } from '../data/legalApi';
import { DOC_FAMILY_LABELS } from '../data/deals';

// React Flow (and its CSS) load only when an admin actually opens this page.
const AdminDiagrams = React.lazy(() => import('../components/AdminDiagrams'));

// Admin handbook — the in-app operator's manual: what the platform does, how
// the engineering works, the core workflows, and the infrastructure it runs
// on. Visible only to org admins/owners (or a superadmin token claim). The
// content is hand-curated from docs/ and brain/wiki — update it when a system
// page changes shape (see brain/wiki/systems/admin-docs.md).

const SECTIONS = [
  {
    id: 'overview',
    icon: '⚖️',
    title: 'What fast.legal is',
    body: (
      <>
        <p>
          fast.legal is an AI-first legal document platform: it ingests real agreements
          (Word, PDF, pasted text), understands them as structured clause arrays with a
          knowledge graph and a per-project vector brain, and then lets AI agents draft,
          refactor, redline, audit, and export them — with every change recorded in an
          immutable, hash-sealed version chain.
        </p>
        <p>
          The unit of work is a <strong>Project</strong> (internally a “deal”): a container of
          documents, versions, deal points, participants, tasks, and an audit trail, owned by
          an organization. Projects carry a type taxonomy (16 types across negotiated
          instruments and authored artifacts) plus free-form tags, so one company can hold
          operating agreements, filings, presentations, and memos side by side.
        </p>
      </>
    ),
  },
  {
    id: 'diagrams',
    icon: '🗺️',
    title: 'Visual maps',
    body: (
      <>
        <p>
          Interactive diagrams of the platform — the system map, feature map, document
          lifecycle, derive engine, deploy paths, and the security model. Pan, zoom, and
          rearrange freely; each tab has a one-paragraph orientation.
        </p>
        <Suspense fallback={<p className="cs-help">Loading diagrams…</p>}>
          <AdminDiagrams />
        </Suspense>
      </>
    ),
  },
  {
    id: 'features',
    icon: '✨',
    title: 'Feature map',
    body: (
      <>
        <ul>
          <li><strong>Document ingest</strong> — upload .docx/.pdf (or paste text); the ingest worker extracts text, splits clauses by heading heuristics, writes immutable version 1, and runs AI smart-import (profile, suggested deal points). Tracked-changes .docx files are detected and reconstructed as two versions (prior draft + markup accepted) with shared clause ids.</li>
          <li><strong>Immutable versions + checkpoints</strong> — publishing always writes a NEW version doc; the checkpoint worker seals each with a chained SHA-256 hash and S3 archive. <code>/legal/verify</code> produces a contract-verification report.</li>
          <li><strong>Redline & explanation rail</strong> — client-side word diffs between any two versions, removed-section strikethroughs, and a document-order side rail explaining each change with its recorded rationale (scroll-synced, click-to-jump).</li>
          <li><strong>Derive engine</strong> — refactor an agreement for a related entity (e.g. HoldCo OA → OpCo OA): entity-cast interview up front, two-pass plan/redraft, per-op rationale in the manifest, v1 = source baseline so redlines and exports just work.</li>
          <li><strong>Purpose audit & agreement ecosystem</strong> — every agent pass carries an ecosystem brief (which agreement lives at which entity layer, what is signed and settled); the purpose audit benchmarks a draft against its role and flags relics, adaptations, misplaced concepts, and missing standard provisions.</li>
          <li><strong>Company structure map</strong> — an AI-compiled diagram of entity layers, ownership/management links, and the agreement network, rendered on the project dashboard.</li>
          <li><strong>Key facts & review queue</strong> — deterministic extraction of entities, jurisdictions, dates, amounts, and addresses from the CURRENT draft; conflicts become explicit review questions with suggested replacements, keep-both rulings, or natural-language instructions to the agent.</li>
          <li><strong>Deal Points Register</strong> — per-party positions (stance, rationale, status) on negotiated points, accept/reject/counter flows, AI compromise suggestions, and apply-to-draft publishing.</li>
          <li><strong>Agent editing modes</strong> — “vibe” (agent applies changes, manifest records everything) and “precision” (agent stages a full proposal for human approval, op by op).</li>
          <li><strong>Exports</strong> — clean .docx, true tracked-changes redline .docx (w:ins/w:del attributed to the editing party), clean PDF (signing-ready, provenance in PDF metadata), and annotated redline PDF with change reasons in the margin.</li>
          <li><strong>Processing cockpit</strong> — a live cognitive trace at <code>/activity/:taskId</code>: typed pipeline events (parse/graph/vector/plan/edit), a Cytoscape knowledge-graph canvas, a retrieval map, and playback scrubbing of the persisted trace.</li>
          <li><strong>Task progress</strong> — every long AI task streams steps, live logs, EWMA-based ETA, and token cost to <code>orgs/{'{org}'}/tasks</code>; the Activity page is the org-wide operations view.</li>
          <li><strong>Project brain</strong> — a per-project embedded index (Amazon S3 Vectors) over documents, deal points, analyses, correspondence, and revision manifests; <code>/legal/ask</code> answers with clause citations; retrains on drift.</li>
          <li><strong>Jurisdictional compliance</strong> — AI screening of clauses across a live-tunable jurisdiction panel (NV, DE, TX, CA, NY, Cayman, EU, US federal), with stored assessment history.</li>
          <li><strong>Office integration</strong> — a Word Add-in task pane (content-control clause mapping, pull/publish versions) and a PowerPoint kit (decks as documents, agentic slide editing, pptxgenjs export).</li>
          <li><strong>Assistants</strong> — a members-only realtime voice agent (ephemeral provider tokens, allowlisted read-only tools) and a RAG chat agent with citations.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'workflows',
    icon: '🔁',
    title: 'Core workflows',
    body: (
      <>
        <p><strong>Ingest a document.</strong> Upload from the project dashboard (multiple formats of the same draft are kept together and SHA-256'd; the ranked source is ingested). The doc queues, the worker claims it, and it flips to ready with version 1, an AI profile, and suggested deal points. Newly uploaded documents are also scanned against the project's open agent questions — filings often answer them.</p>
        <p><strong>Receive a counterparty turn.</strong> “Upload new draft” on a document re-queues it; clauses are aligned to existing ids (numbering-insensitive), a new version publishes with a full manifest, and an async AI change brief lands on the version (summary, risk notes, key asks cross-checked against pasted correspondence).</p>
        <p><strong>Derive a related agreement.</strong> Pick a source document, state the brief (“make this the OpCo OA for Netiva, LLC”). The entity interview proposes the cast (Company, Member, Manager, governing law) and asks only about genuine ambiguities. The two-pass engine plans per-section verdicts (keep/adapt/remove), redrafts in batches, and publishes with per-op rationale — watch it live in the cockpit.</p>
        <p><strong>Audit and polish.</strong> Run the purpose audit; stage all fixes as one precision proposal or apply as a vibe edit. Resolve key-fact conflicts in the review queue. Every publish is CAS-checked against the version you started from, so concurrent work can never silently clobber.</p>
        <p><strong>Finalize.</strong> Export clean and redline artifacts (.docx and PDF). The clean PDF is signing-ready; version provenance lives in PDF document properties, not on the page.</p>
      </>
    ),
  },
  {
    id: 'engineering',
    icon: '🛠️',
    title: 'Engineering',
    body: (
      <>
        <ul>
          <li><strong>Shape</strong> — React 18 SPA (CRA) on Firebase Hosting talks directly to Firebase Auth + Firestore; a Node 22 ESM backend on ECS Fargate exposes the upload/AI APIs and runs Firestore-watching workers. The same image doubles as the ephemeral fan-out runner.</li>
          <li><strong>Firestore is the DB and the job queue</strong> — the UI writes a status flag, a worker claims the doc in a transaction (feed/claim/heartbeat/sweep template), processes, writes results back; the UI reacts via onSnapshot. No REST CRUD layer.</li>
          <li><strong>Entity registry</strong> — one declarative definition (<code>backend/shared/entities/registry.js</code>) generates Firestore security rules, GraphQL CRUD, the store adapter, and the client accessor registry. Edit the registry, run the generator, never hand-edit generated code.</li>
          <li><strong>Write safety</strong> — <code>commitVersionTx</code> publishes versions transactionally with a compare-and-swap on <code>currentVersionId</code>; long AI edits take a soft edit lease (heartbeat-extended TTL) on the work doc; a zombie-task sweep frees leases from crashed workers.</li>
          <li><strong>Large payload spillover</strong> — oversized clause arrays and graphs move to S3 with Firestore pointers; one load helper (<code>loadVersionClauses</code>) everywhere.</li>
          <li><strong>Retrieval</strong> — per-project brain on Amazon S3 Vectors (documents, deal points, analyses, revision manifests as chunks; echo-chamber guard keeps derived text from polluting recall); org knowledge corpus on Firestore native vector search with per-chunk audience gating.</li>
          <li><strong>Document graph</strong> — per-doc defined terms, cross-references, and similarity edges rebuilt by the graph worker on every new version; powers the clause Links panel, editor ripple check, and cockpit canvas.</li>
          <li><strong>LLM layer</strong> — <code>chatComplete</code> with live-configurable provider/model, per-task usage metering, timeouts, provider request-id capture on errors, and an automatic fallback model for transient 404 flaps.</li>
          <li><strong>Live config</strong> — every tunable (models, concurrency, timeouts, jurisdiction panel) lives in the Firestore <code>config/app</code> doc and applies within ~1 minute. Redeploying to change a number means the number is in the wrong place.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'infra',
    icon: '🏗️',
    title: 'Infrastructure & deploys',
    body: (
      <>
        <ul>
          <li><strong>Identity</strong> — everything is namespaced by <code>PROJECT_SLUG</code> from <code>project.env</code>: S3 buckets, ECS cluster/service, log groups, SSM/Secrets paths. No hardcoded resource names.</li>
          <li><strong>Frontend</strong> — CRA build → Firebase Hosting (<code>scripts/deploy-frontend.sh</code>). Nothing secret ships in the bundle; Firestore rules are the security boundary. Custom domain (fast.legal) is wired via the Hosting API + GoDaddy DNS.</li>
          <li><strong>Backend</strong> — two deploy paths: fast S3 code overlay (~1 min, JS-only changes, extracted over the baked image at task start) and full CodeBuild rebuild (~5–8 min, required for dependency/Dockerfile changes; a deps-fingerprint guard blocks mismatches). Both roll with 100% minimum healthy + circuit breaker: zero downtime.</li>
          <li><strong>Fan-out</strong> — heavy jobs (bulk ingest, corpus indexing) run as a <code>jobs/{'{id}'}</code> doc driving prep → N parallel Fargate chunk tasks → stitch, with per-task telemetry and a <code>jobControls</code> kill switch every long job honors.</li>
          <li><strong>Secrets</strong> — resolved at runtime: env → SSM <code>/&lt;slug&gt;/*</code> → Secrets Manager. Never in git, never in the browser.</li>
          <li><strong>Email</strong> — SES with the fast.legal DKIM identity sends invite emails (send-once claim on invite docs, live-config kill switch).</li>
          <li><strong>Verification discipline</strong> — a deploy is verified by the artifact it produces (the S3 object, the Firestore doc, the exported PDF), never by a green log line alone.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'security',
    icon: '🔐',
    title: 'Security & tenancy',
    body: (
      <>
        <ul>
          <li><strong>Org tenancy</strong> — membership docs at <code>orgs/{'{orgId}'}/members/{'{uid}'}</code> are the source of truth; roles ascend member → admin → owner. Generated rules resolve the caller's role from them directly (no claim staleness).</li>
          <li><strong>Custom claims</strong> — the claims worker mirrors memberships into an <code>orgs</code> token claim for server surfaces (AI endpoints, GraphQL) to skip a lookup; a <code>superadmin</code> claim exists for cross-org operator access.</li>
          <li><strong>Invites</strong> — top-level invite docs (token id) let an invited email accept without org access; the worker creates the membership with the admin SDK and emails via SES.</li>
          <li><strong>serverOnly fields</strong> — AI-written state (profiles, audits, suggestions, checkpoints) is admin-SDK-only; generated rules reject any client write that touches it.</li>
          <li><strong>Agent tool safety</strong> — voice/chat agent tools are allowlisted in a code registry at the permission level; safety never depends on prompt instructions.</li>
          <li><strong>Deal-level roles</strong> — participant roles (viewer/commenter/editor/approver/admin) are enforced by app surfaces; org membership is the storage boundary in v1. Private per-party workspaces would need rule-level party isolation first.</li>
          <li><strong>This page</strong> — rendered only for org admins/owners (or a superadmin claim). It contains operational detail, not secrets; the deep-dive sources live in the repo under <code>docs/</code> and <code>brain/</code>.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'operations',
    icon: '🎛️',
    title: 'Operating the platform',
    body: (
      <>
        <ul>
          <li><strong>Activity</strong> (<code>/activity</code>) — org-wide view of every AI task: status, steps, live logs, ETA, cost. Click into any task for the cockpit trace.</li>
          <li><strong>Jobs</strong> (<code>/jobs</code>) — fan-out job telemetry with pause/resume/cancel kill switches.</li>
          <li><strong>Tuning</strong> — change models, concurrency, timeouts, or the jurisdiction panel in the Firestore <code>config/app</code> doc; it applies live. Backend redeploys are only for code.</li>
          <li><strong>Stuck task?</strong> — the zombie sweep marks stale running tasks as error and frees their edit leases automatically; a re-queue after that is safe because publishes are CAS-guarded.</li>
          <li><strong>Adding members</strong> — invite from the org switcher; admins can invite members/admins, the claims worker handles membership + email. Roles can be edited on the membership doc.</li>
        </ul>
      </>
    ),
  },
];

// Suggested platform types — org-coined document types that crossed the
// live-config thresholds (docTypes.promoteMinUsage / promoteMinOrgs). Promote
// copies the type to the platform catalog; Merge folds it into an existing
// platform type as aliases; Dismiss hides it. Superadmin only (server-enforced).
function DocTypeQueue({ superadmin }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState('');
  const [mergeInto, setMergeInto] = useState({});
  const load = () => docTypeQueue().then(setData).catch((e) => setErr(e.message));
  useEffect(() => { if (superadmin && isAiConfigured()) load(); }, [superadmin]);
  if (!superadmin) return <p className="cs-help">Org-coined document types reaching the usage thresholds are reviewed here by platform operators (superadmin claim).</p>;
  if (!isAiConfigured()) return <p className="cs-help">Backend API not configured.</p>;
  if (err) return <p className="cs-help">Could not load the queue: {err}</p>;
  if (!data) return <div className="cs-skeleton" style={{ height: 80 }} />;
  const act = async (key, action, extra = {}) => {
    setBusy(`${key}:${action}`);
    try {
      await docTypeAction({ key, action, ...extra });
      toast(action === 'promote' ? `"${key}" is now a platform type` : action === 'merge' ? `"${key}" merged into ${extra.intoKey}` : `"${key}" dismissed`, 'ok');
      await load();
    } catch (e) { toast(e.message, 'danger'); } finally { setBusy(''); }
  };
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }} data-nova-note="Doc-type promotion queue: Promote / Merge into / Dismiss org-coined types">
      <p className="cs-help">
        Thresholds (live config <code>docTypes.*</code>): used ≥ {data.thresholds.minUsage}× in one org, or coined by ≥ {data.thresholds.minOrgs} orgs.
        Platform catalog: {data.platform.length} types.
      </p>
      {!data.queue.length && <p className="u-muted u-small">Nothing waiting — every org type is below threshold or already reviewed.</p>}
      {data.queue.length > 0 && (
        <table className="cs-table">
          <thead><tr><th>Type</th><th>Family</th><th>Orgs</th><th>Uses</th><th>Action</th></tr></thead>
          <tbody>
            {data.queue.map((g) => (
              <tr key={g.key}>
                <td><strong>{g.label}</strong><div className="u-xs u-muted">{g.key}{g.aliases?.length ? ` · aka ${g.aliases.join(', ')}` : ''}</div></td>
                <td className="u-small">{DOC_FAMILY_LABELS[g.family] || g.family} · {g.view || 'general'}</td>
                <td className="u-small u-tabular">{g.orgs.length}</td>
                <td className="u-small u-tabular">{g.usage}</td>
                <td>
                  <div className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
                    <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!!busy} onClick={() => act(g.key, 'promote')}>{busy === `${g.key}:promote` ? '…' : 'Promote'}</button>
                    <select className="cs-input" style={{ maxWidth: 200, minHeight: 28, padding: '2px 6px', fontSize: 12 }} value={mergeInto[g.key] || ''} onChange={(e) => setMergeInto({ ...mergeInto, [g.key]: e.target.value })} aria-label={`Merge ${g.label} into`}>
                      <option value="">Merge into…</option>
                      {data.platform.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                    <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!busy || !mergeInto[g.key]} onClick={() => act(g.key, 'merge', { intoKey: mergeInto[g.key] })}>Merge</button>
                    <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!!busy} onClick={() => act(g.key, 'dismiss')}>Dismiss</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function AdminDocs() {
  const { user } = useAuth();
  const { role } = useOrg();
  const [superadmin, setSuperadmin] = useState(false);

  useEffect(() => {
    let on = true;
    user?.getIdTokenResult?.().then((r) => { if (on) setSuperadmin(r?.claims?.superadmin === true); }).catch(() => {});
    return () => { on = false; };
  }, [user]);

  const allowed = superadmin || role === 'admin' || role === 'owner';

  if (!allowed) {
    return (
      <div className="cs-page">
        <div className="cs-page-head"><h1>Platform docs</h1></div>
        <p className="cs-help">This handbook is available to organization admins only. Ask an org owner to promote your membership if you need access.</p>
      </div>
    );
  }

  return (
    <div className="cs-page cs-docs">
      <div className="cs-page-head">
        <h1>📚 Platform handbook</h1>
        <p className="cs-help">
          The operator's manual for fast.legal — features, workflows, engineering, infrastructure,
          and security. Admins only. Deep-dive sources live in the repo (<code>docs/</code>, <code>brain/</code>).
        </p>
      </div>

      <div className="cs-docs-layout">
        <nav className="cs-docs-toc" aria-label="Handbook contents">
          {SECTIONS.map((s) => (
            <a key={s.id} href={`#${s.id}`} className="cs-docs-toc-link">
              <span aria-hidden="true">{s.icon}</span> {s.title}
            </a>
          ))}
          <a href="#doc-types" className="cs-docs-toc-link"><span aria-hidden="true">🏷️</span> Suggested platform types</a>
        </nav>

        <div className="cs-docs-body">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="cs-card cs-docs-section">
              <h2><span aria-hidden="true">{s.icon}</span> {s.title}</h2>
              {s.body}
            </section>
          ))}
          <section id="doc-types" className="cs-card cs-docs-section">
            <h2><span aria-hidden="true">🏷️</span> Suggested platform types</h2>
            <p>
              Every organization can coin its own document types from the upload picker. Types that
              catch on — used repeatedly in one org, or coined independently by several — are proposed
              here for the platform catalog, so every project improves the spectrum for everyone.
            </p>
            <DocTypeQueue superadmin={superadmin} />
          </section>
        </div>
      </div>
    </div>
  );
}
